import { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { useRefreshWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';
import type { CoinbaseEngUsersAccountManagementApiApiLegalEntityRole } from '@cbhq/identity-api-hooks/account-management/types';

import { useOrganization } from '../../../contexts/OrganizationContext';
import type { EditMemberStatus } from '../components/EditMemberTray/EditMemberTrayHeader';
import { MultiLEProductAccessGroup } from '../components/InviteTray/MultiLEProductAccessGroup';
import { PRODUCTS } from '../components/InviteTray/productCatalog';
import { SaveFooter } from '../components/SaveFooter';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useEditSubmission } from '../useEditSubmission';
import { useMultiLEProductAccessEntities } from '../useMultiLEProductAccessEntities';
import type { EntityIdToProductSelections, ProductSelections } from '../useMultiLEProductSelection';
import { useMultiLEProductSelection } from '../useMultiLEProductSelection';
import type { Member as TeamMember, MemberRole } from '../utils/constants';
import { getProductKey, useRoles } from '../utils/useRoles';

import { EditMemberTray } from './EditMemberTray';

const messages = defineMessages({
  saveButton: {
    id: 'Team.multiLEEditTray.saveButton',
    defaultMessage: 'Save changes',
    description: 'Save button in the multi-LE edit member tray',
  },
  saveSuccess: {
    id: 'Team.multiLEEditTray.saveSuccess',
    defaultMessage: 'Membership updated',
    description: 'Toast shown after successfully saving member role changes',
  },
  saveError: {
    id: 'Team.multiLEEditTray.saveError',
    defaultMessage: 'Error updating membership',
    description: 'Toast shown when saving member role changes fails',
  },
});

type MultiLEEditTrayProps = {
  member: TeamMember;
  memberLegalEntityRoles?: CoinbaseEngUsersAccountManagementApiApiLegalEntityRole[];
  status: EditMemberStatus;
  joinedLabel?: string;
  lastActiveLabel?: string;
  onClose: () => void;
};

export function MultiLEEditTray({
  member,
  memberLegalEntityRoles,
  status,
  joinedLabel,
  lastActiveLabel,
  onClose,
}: MultiLEEditTrayProps) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const trayRef = useRef<TrayRefProps>(null);
  const [alertVisible, setAlertVisible] = useState(false);
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({ organizationId });
  const { rolesByProduct, supportedRoles } = useRoles();

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  const localizedProducts = useMemo(
    () =>
      PRODUCTS.map(({ titleMessage, descriptionMessage, ...product }) => ({
        ...product,
        title: formatMessage(titleMessage),
        description: formatMessage(descriptionMessage),
      })),
    [formatMessage],
  );

  const productKeys = useMemo(
    () => localizedProducts.map(({ productKey }) => productKey),
    [localizedProducts],
  );

  const supportedRolesSet = useMemo(() => new Set(supportedRoles), [supportedRoles]);

  const initialEntitySelections = useMemo(() => {
    const result: EntityIdToProductSelections = {};
    for (const ler of memberLegalEntityRoles ?? []) {
      if (ler.legalEntityId) {
        const productSelections: ProductSelections = {};
        for (const role of (ler.roles ?? []) as MemberRole[]) {
          if (supportedRolesSet.has(role)) {
            const key = getProductKey(role);
            if (key) {
              (productSelections[key] ??= []).push(role);
            }
          }
        }
        result[ler.legalEntityId] = productSelections;
      }
    }
    return result;
  }, [memberLegalEntityRoles, supportedRolesSet]);

  // Roles outside supportedRoles must be preserved when saving because
  // updateOrganizationMemberRole() replaces the full role set for each entity.
  const hiddenRolesByEntity = useMemo(() => {
    const result = new Map<string, MemberRole[]>();
    for (const ler of memberLegalEntityRoles ?? []) {
      if (ler.legalEntityId) {
        const hidden = ((ler.roles ?? []) as MemberRole[]).filter(
          (role) => !supportedRolesSet.has(role),
        );
        if (hidden.length > 0) {
          result.set(ler.legalEntityId, hidden);
        }
      }
    }
    return result;
  }, [memberLegalEntityRoles, supportedRolesSet]);

  const multiLEProductSelection = useMultiLEProductSelection({
    legalEntityIds,
    productKeys,
    rolesByProduct,
    initialEntitySelections,
  });
  const { hasChanges, getLegalEntityRoles } = multiLEProductSelection;

  const entities = useMultiLEProductAccessEntities({
    legalEntities: selectedOrganization?.legalEntities ?? [],
    localizedProducts,
    rolesByProduct,
    selection: multiLEProductSelection,
  });

  const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;

  const legalEntityRoles = useMemo(
    () =>
      getLegalEntityRoles().map(({ legalEntityId, roles }) => ({
        legalEntityId,
        roles: [...roles, ...(hiddenRolesByEntity.get(legalEntityId) ?? [])],
      })),
    [getLegalEntityRoles, hiddenRolesByEntity],
  );

  const handleSaveSuccess = useCallback(() => {
    toast.show(formatMessage(messages.saveSuccess));
    void refreshWorkspaceMembers();
    onClose();
  }, [formatMessage, onClose, refreshWorkspaceMembers, toast]);

  const handleSaveFailure = useCallback(() => {
    toast.show(formatMessage(messages.saveError));
  }, [formatMessage, toast]);

  const { isSaving, saveEdit } = useEditSubmission({
    organizationId,
    memberUuid: member.userUuid,
    legalEntityRoles,
    isFormValid: hasChanges,
    onSuccess: handleSaveSuccess,
    onFailure: handleSaveFailure,
  });

  const handleBeforeClose = useCallback(() => {
    if (hasChanges) {
      setAlertVisible(true);
      return;
    }

    trayRef.current?.close();
  }, [hasChanges]);

  const handleKeepEditing = useCallback(() => {
    setAlertVisible(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setAlertVisible(false);
    trayRef.current?.close();
  }, []);

  const footer = useMemo(
    () => (
      <SaveFooter visible={hasChanges || isSaving} onSave={saveEdit} loading={isSaving}>
        {formatMessage(messages.saveButton)}
      </SaveFooter>
    ),
    [formatMessage, hasChanges, isSaving, saveEdit],
  );

  return (
    <>
      <EditMemberTray
        name={name}
        email={member.email}
        status={status}
        joinedLabel={joinedLabel}
        lastActiveLabel={lastActiveLabel}
        avatarSrc={member.avatarUrl}
        trayRef={trayRef}
        footer={footer}
        beforeClose={handleBeforeClose}
        onClose={onClose}
      >
        <MultiLEProductAccessGroup entities={entities} />
      </EditMemberTray>
      <UnsavedChangesAlert
        variant="edit"
        visible={alertVisible}
        memberName={name}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
